import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  CalendarCheck, Plus, Trash2, Play, Loader2, Gift, Info, Pencil, X, Check,
} from 'lucide-react';
import axios from 'axios';
import { toast } from '../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (iso) => {
  if (!iso) return '';
  try { return new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
};
const addDays = (iso, n) => {
  const d = new Date(iso + 'T00:00:00'); d.setDate(d.getDate() + n - 1);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
};

export default function PublicHolidays() {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', start_date: '', days: 1, expiry_date: '' });
  const [saving, setSaving] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [editing, setEditing] = useState(null); // holiday id being edited
  const [editForm, setEditForm] = useState({ name: '', start_date: '', days: 1, expiry_date: '' });

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/api/public-holidays`, { headers })
      .then(r => setHolidays(r.data || []))
      .catch(() => toast({ title: 'Failed to load holidays', variant: 'destructive' }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const addHoliday = async () => {
    if (!form.name.trim() || !form.start_date) {
      toast({ title: 'Name and start date are required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/api/public-holidays`, {
        name: form.name.trim(),
        start_date: form.start_date,
        days: Number(form.days) || 1,
        expiry_date: form.expiry_date || null,
      }, { headers });
      toast({ title: 'Holiday added' });
      setForm({ name: '', start_date: '', days: 1, expiry_date: '' });
      load();
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const startEdit = (h) => {
    setEditing(h.id);
    setEditForm({ name: h.name, start_date: h.start_date, days: h.days, expiry_date: h.expiry_date || '' });
  };

  const saveEdit = async (id) => {
    try {
      await axios.put(`${API}/api/public-holidays/${id}`, {
        name: editForm.name,
        start_date: editForm.start_date,
        days: Number(editForm.days) || 1,
        expiry_date: editForm.expiry_date === '' ? '' : editForm.expiry_date,
      }, { headers });
      toast({ title: 'Holiday updated' });
      setEditing(null);
      load();
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const removeHoliday = async (h) => {
    const delCredits = h.credited_count > 0
      ? window.confirm(`Delete "${h.name}"?\n\nThis holiday has awarded ${h.credited_count} PH credit(s).\n\nOK = also remove those credits.\nCancel = keep the holiday.`)
      : window.confirm(`Delete "${h.name}"?`);
    // For holidays without credits, a single confirm is enough; bail if cancelled.
    if (h.credited_count === 0 && !delCredits) return;
    if (h.credited_count > 0 && !delCredits) {
      // user pressed Cancel → keep everything
      return;
    }
    try {
      await axios.delete(`${API}/api/public-holidays/${h.id}`, {
        headers, params: { delete_credits: h.credited_count > 0 },
      });
      toast({ title: 'Holiday deleted' });
      load();
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const runNow = async () => {
    setProcessing(true);
    try {
      const r = await axios.post(`${API}/api/public-holidays/process`, {}, { headers });
      const n = r.data?.credits_added || 0;
      toast({
        title: n > 0 ? `${n} PH credit(s) awarded` : 'No new credits',
        description: n > 0
          ? `Processed through ${fmt(r.data.processed_through)}.`
          : 'Everyone who worked a past holiday is already credited.',
      });
      load();
    } catch (e) {
      toast({ title: 'Processing failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally { setProcessing(false); }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-5" data-testid="public-holidays-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <CalendarCheck className="h-7 w-7 text-violet-600" /> Public Holidays
          </h1>
          <p className="text-gray-600 mt-1 text-sm">
            Define yearly public holidays. Staff who complete a clock IN &amp; OUT on a holiday date
            automatically earn 1 PH credit per day worked.
          </p>
        </div>
        <Button onClick={runNow} disabled={processing} className="bg-violet-600 hover:bg-violet-700" data-testid="ph-run-now">
          {processing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
          Run credit awarding now
        </Button>
      </div>

      {/* Add form */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><Plus className="h-4 w-4 text-violet-600" /> Add a public holiday</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
            <div className="md:col-span-2">
              <Label className="text-xs">Holiday name</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Eid Al Fitr" data-testid="ph-name" />
            </div>
            <div>
              <Label className="text-xs">Start date</Label>
              <Input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} data-testid="ph-start" />
            </div>
            <div>
              <Label className="text-xs">No. of days</Label>
              <Input type="number" min="1" max="60" value={form.days} onChange={e => setForm(f => ({ ...f, days: e.target.value }))} data-testid="ph-days" />
            </div>
            <div>
              <Label className="text-xs">Credit expiry <span className="text-gray-400">(optional)</span></Label>
              <Input type="date" value={form.expiry_date} onChange={e => setForm(f => ({ ...f, expiry_date: e.target.value }))} data-testid="ph-expiry" />
            </div>
          </div>
          <div className="flex items-center justify-between mt-3">
            <p className="text-[11px] text-gray-400 flex items-center gap-1">
              <Info className="h-3 w-3" /> Leave "Credit expiry" blank for credits that never expire — you can set/adjust expiry per staff later.
            </p>
            <Button onClick={addHoliday} disabled={saving} size="sm" data-testid="ph-add">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />} Add holiday
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-gray-500 flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
          ) : holidays.length === 0 ? (
            <div className="p-10 text-center text-gray-400" data-testid="ph-empty">
              <Gift className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              No public holidays defined yet. Add your first one above.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-600">
                <tr>
                  <th className="text-left p-3">Holiday</th>
                  <th className="text-left p-3">Dates</th>
                  <th className="text-left p-3">Days</th>
                  <th className="text-left p-3">Credit expiry</th>
                  <th className="text-left p-3">Credited</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {holidays.map(h => editing === h.id ? (
                  <tr key={h.id} className="border-t bg-violet-50/40" data-testid={`ph-edit-row-${h.id}`}>
                    <td className="p-2"><Input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} className="h-8" /></td>
                    <td className="p-2"><Input type="date" value={editForm.start_date} onChange={e => setEditForm(f => ({ ...f, start_date: e.target.value }))} className="h-8" /></td>
                    <td className="p-2"><Input type="number" min="1" max="60" value={editForm.days} onChange={e => setEditForm(f => ({ ...f, days: e.target.value }))} className="h-8 w-16" /></td>
                    <td className="p-2"><Input type="date" value={editForm.expiry_date} onChange={e => setEditForm(f => ({ ...f, expiry_date: e.target.value }))} className="h-8" /></td>
                    <td className="p-2 text-gray-400">{h.credited_count}</td>
                    <td className="p-2 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" className="h-8 text-emerald-600" onClick={() => saveEdit(h.id)} data-testid={`ph-save-${h.id}`}><Check className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" className="h-8 text-gray-500" onClick={() => setEditing(null)}><X className="h-4 w-4" /></Button>
                    </td>
                  </tr>
                ) : (
                  <tr key={h.id} className="border-t hover:bg-gray-50" data-testid={`ph-row-${h.id}`}>
                    <td className="p-3 font-medium text-gray-900">{h.name}</td>
                    <td className="p-3 text-gray-600">
                      {fmt(h.start_date)}{h.days > 1 && <span className="text-gray-400"> → {addDays(h.start_date, h.days)}</span>}
                    </td>
                    <td className="p-3"><Badge variant="outline">{h.days}</Badge></td>
                    <td className="p-3">
                      {h.expiry_date
                        ? <span className="text-gray-700">{fmt(h.expiry_date)}</span>
                        : <Badge variant="outline" className="text-emerald-600 border-emerald-300">No expiry</Badge>}
                    </td>
                    <td className="p-3">
                      {h.credited_count > 0
                        ? <Badge className="bg-violet-100 text-violet-700">{h.credited_count}</Badge>
                        : <span className="text-gray-400">0</span>}
                    </td>
                    <td className="p-3 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" className="h-8 text-gray-500 hover:text-violet-600" onClick={() => startEdit(h)} data-testid={`ph-edit-${h.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" className="h-8 text-gray-500 hover:text-red-600" onClick={() => removeHoliday(h)} data-testid={`ph-delete-${h.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <p className="text-[11px] text-gray-400 px-1">
        Credits are awarded automatically every day from the Clock In/Out attendance, and you can trigger it
        anytime with "Run credit awarding now". Awarding is idempotent — no one is credited twice for the same day.
        Awarded credits appear in each employee's <b>Public Holiday Credits</b> section (Leaves page).
      </p>
    </div>
  );
}
